/**
 * Deal アンドゥ二重保護 (Phase 9) hooks。
 *
 * - useTrashDeals: _deleted=true のリスト
 * - useRestoreDeletedDeal: 30分以内 (本人)
 * - useAdminRecoverDeal: 5営業日以内 (owner)
 */
import type { Deal } from '@goenhub/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

import { db, functions } from '@/lib/firebase';

export interface TrashedDealRecord extends Deal {
  id: string;
}

export function useTrashDeals() {
  return useQuery({
    queryKey: ['deals', 'trash'],
    queryFn: async (): Promise<TrashedDealRecord[]> => {
      const snap = await getDocs(
        query(
          collection(db, 'deals'),
          where('_deleted', '==', true),
          orderBy('_deletedAt', 'desc'),
        ),
      );
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Deal) }));
    },
  });
}

const deleteFn = httpsCallable<{ dealId: string }, { id: string }>(
  functions,
  'deleteDeal',
);

const restoreFn = httpsCallable<{ dealId: string }, { id: string }>(
  functions,
  'restoreDeletedDeal',
);

const adminRecoverFn = httpsCallable<{ dealId: string }, { id: string }>(
  functions,
  'adminRecoverDeal',
);

/**
 * 商談をソフト削除 (ゴミ箱へ移動)。editor+ 権限。
 * 削除後は 30分以内 即時 undo / 5営業日以内 管理者復元が可能。
 */
export function useDeleteDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dealId: string) => {
      const r = await deleteFn({ dealId });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'trash'] });
    },
  });
}

export function useRestoreDeletedDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dealId: string) => {
      const r = await restoreFn({ dealId });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'trash'] });
    },
  });
}

export function useAdminRecoverDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (dealId: string) => {
      const r = await adminRecoverFn({ dealId });
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      qc.invalidateQueries({ queryKey: ['deals', 'trash'] });
    },
  });
}
